import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { orderView, paymentLabel, isDigitalPayment, orderNumber } from '../utils/orders';
import { LogOut, MapPin, CheckCircle, Navigation, Package, DollarSign, Clock, XCircle } from 'lucide-react';
import { formatStaffCities } from '../data/colombiaCities';

export default function DeliveryPortal() {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [driverData, setDriverData] = useState(null);
  
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('available'); // 'available' or 'my_orders'

  // La sesión del domiciliario se guarda en el teléfono: { id, name, business_id, pin }. Las sesiones antiguas guardaban la fila completa de staff (con pin_code).
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('keyla_driver_session') || 'null');
      const pinSaved = saved?.pin || saved?.pin_code;
      if (saved?.id && pinSaved) {
        setDriverData({ id: String(saved.id), name: saved.name, business_id: saved.business_id, ciudad: saved.ciudad || '', pin: String(pinSaved) });
        setIsAuthenticated(true);
      }
    } catch {
      localStorage.removeItem('keyla_driver_session');
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('keyla_driver_session');
    setDriverData(null);
    setIsAuthenticated(false);
    setAvailableOrders([]);
    setMyOrders([]);
  };

  // Las tablas de pedidos no se abren al público: todo pasa por funciones de la base que comprueban el PIN y solo tocan los pedidos del negocio del domiciliario.
  const fetchOrders = async (driver) => {
    const { data, error: rpcError } = await supabase.rpc('delivery_orders', { p_staff: driver.id, p_pin: driver.pin });
    if (rpcError) {
      console.error("Error fetching orders:", rpcError);
      if (rpcError.code === '28000') handleLogout(); // el PIN cambió o el domiciliario ya no existe
      else if (['PGRST202', '42883'].includes(rpcError.code)) setError('Falta ejecutar la migración 20261006c_delivery_portal.sql en Supabase.');
      return;
    }
    // Con PIN malo o bloqueado la base responde un objeto con `error` (no lanza excepción, para poder contar los intentos).
    if (data?.error === 'credenciales') { handleLogout(); return; }
    if (data?.error === 'bloqueado') { setError('Demasiados intentos con un PIN equivocado. Espera 15 minutos.'); return; }
    setError(null);
    setAvailableOrders(Array.isArray(data?.available) ? data.available : []);
    setMyOrders(Array.isArray(data?.mine) ? data.mine : []);
  };

  useEffect(() => {
    if (isAuthenticated && driverData) {
      fetchOrders(driverData);

      // Poll for new orders
      const interval = setInterval(() => {
        fetchOrders(driverData);
      }, 15000); // every 15s
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, driverData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: loginError } = await supabase.rpc('delivery_login', { p_phone: phone, p_pin: pin });
      if (data?.error === 'bloqueado') throw new Error('Demasiados intentos con un PIN equivocado. Espera 15 minutos e intenta de nuevo.');
      if (loginError || !data?.id) {
        throw new Error(['PGRST202', '42883'].includes(loginError?.code)
          ? 'Falta ejecutar la migración 20261006c_delivery_portal.sql en Supabase.'
          : 'Credenciales inválidas. Verifica tu teléfono y PIN.');
      }
      const session = { id: String(data.id), name: data.name, business_id: data.business_id, ciudad: data.ciudad || '', pin };
      localStorage.setItem('keyla_driver_session', JSON.stringify(session));
      setDriverData(session);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  // Pedido que se está cerrando: { order, kind: 'deliver' | 'fail' }
  const [closing, setClosing] = useState(null);
  const [closingBusy, setClosingBusy] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [failNote, setFailNote] = useState('');

  const takeOrder = async (orderId) => {
    if (!driverData) return;
    const { data: taken, error: takeError } = await supabase.rpc('delivery_take_order', { p_staff: driverData.id, p_pin: driverData.pin, p_order: String(orderId) });
    if (!takeError && taken === false) { handleLogout(); return; } // PIN cambiado o bloqueado: vuelve a entrar
    if (takeError) {
      console.error("Error al tomar pedido:", takeError);
      alert(takeError.code === '22023' ? 'Ese pedido ya fue tomado, ya no está listo o es de otra ciudad.' : `Error al tomar el pedido: ${takeError.message || takeError.code || 'desconocido'}`);
      fetchOrders(driverData);
      return;
    }
    await fetchOrders(driverData);
    setActiveTab('my_orders');
  };

  // Cierra una entrega: 'delivered' (con o sin cobro en efectivo) o 'cancelled_by_client' / 'cancelled' con el motivo.
  const closeOrder = async (outcome, { paid = false, note = '' } = {}) => {
    if (!driverData || !closing) return;
    setClosingBusy(true);
    const orderId = closing.order.id;
    let { data: done, error: doneError } = await supabase.rpc('delivery_close_order', { p_staff: driverData.id, p_pin: driverData.pin, p_order: String(orderId), p_outcome: outcome, p_paid: paid, p_note: note || null });
    // Sin la migración del cierre de entregas, «Entregado» sigue funcionando con la función de antes (sin registrar el cobro).
    if (doneError && ['PGRST202', '42883'].includes(doneError.code) && outcome === 'delivered') {
      ({ data: done, error: doneError } = await supabase.rpc('delivery_mark_delivered', { p_staff: driverData.id, p_pin: driverData.pin, p_order: String(orderId) }));
    }
    setClosingBusy(false);
    if (!doneError && done === false) { handleLogout(); return; }
    if (doneError) {
      console.error('Error al cerrar la entrega:', doneError);
      alert(['PGRST202', '42883'].includes(doneError.code)
        ? 'Falta ejecutar la migración 20261008d_driver_close_order.sql en Supabase.'
        : doneError.code === '22023' ? 'Este pedido ya no está en camino con tu usuario.' : `Error al actualizar el pedido: ${doneError.message || doneError.code || 'desconocido'}`);
      setClosing(null);
      fetchOrders(driverData);
      return;
    }
    setClosing(null);
    setFailReason(''); setFailNote('');
    fetchOrders(driverData);
  };

  const openWaze = (address) => {
    if (!address) return;
    const url = `https://waze.com/ul?q=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white max-w-sm w-full p-8 rounded-3xl shadow-lg border border-slate-100">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="text-blue-600" size={32} />
            </div>
            <h1 className="text-2xl font-black text-slate-900">Delivery Portal</h1>
            <p className="text-slate-500 text-sm mt-2">Acceso exclusivo para repartidores</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Teléfono</label>
              <input 
                type="tel" 
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="Ej. 573001234567"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">PIN de Acceso</label>
              <input 
                type="password" 
                maxLength={4}
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center tracking-[1em] font-bold text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="****"
                required
              />
            </div>
            
            {error && (
              <div className="bg-rose-50 text-rose-600 text-sm p-3 rounded-xl font-medium text-center">
                {error}
              </div>
            )}

            <button 
              type="submit"
              disabled={loading || !phone || pin.length < 4}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-3.5 rounded-xl shadow-md transition-all active:scale-95 flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                'Ingresar'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard calculations
  const todaysDeliveries = myOrders.filter(o => o.status === 'delivered');
  // Recaudo = efectivo que el domiciliario dijo haber cobrado (las transferencias las confirma el negocio).
  const moneyCollected = todaysDeliveries.reduce((acc, curr) => acc + (curr.is_paid && !isDigitalPayment(orderView(curr).payment) ? Number(curr.total || 0) : 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <h1 className="font-black text-slate-900 leading-tight">Delivery</h1>
              <p className="text-xs text-slate-500 font-medium">Hola, {driverData?.name}{driverData?.ciudad ? ` · ${formatStaffCities(driverData.ciudad)}` : ''}</p>
            </div>
          </div>
          <button 
            onClick={handleLogout}
            className="w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center text-slate-600 transition-colors"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 mt-2">
        {error && <div role="alert" className="bg-amber-50 text-amber-700 text-sm p-3 rounded-xl font-medium mb-4">{error}</div>}
        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <CheckCircle size={14} className="text-green-500" />
              <span className="text-xs font-bold uppercase tracking-wider">Entregas</span>
            </div>
            <p className="text-2xl font-black text-slate-900">{todaysDeliveries.length}</p>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <DollarSign size={14} className="text-blue-500" />
              <span className="text-xs font-bold uppercase tracking-wider">Efectivo cobrado</span>
            </div>
            <p className="text-2xl font-black text-slate-900">${moneyCollected.toLocaleString()}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex p-1 bg-slate-200 rounded-xl mb-4">
          <button 
            onClick={() => setActiveTab('available')}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'available' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Disponibles ({availableOrders.length})
          </button>
          <button 
            onClick={() => setActiveTab('my_orders')}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'my_orders' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Mis Entregas ({myOrders.filter(o => o.status === 'shipped').length})
          </button>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {activeTab === 'available' && (
            availableOrders.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package size={48} className="mx-auto mb-4 opacity-20" />
                <p className="font-medium">No hay pedidos listos para entregar.</p>
              </div>
            ) : (
              availableOrders.map(order => (
                <div key={order.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-xs font-bold text-slate-400">Pedido {orderNumber(order)}</span>
                      <h3 className="font-black text-lg text-slate-900 mt-0.5">{orderView(order).name || 'Cliente'}</h3>
                    </div>
                    <span className="bg-amber-100 text-amber-700 text-[10px] uppercase font-bold px-2 py-1 rounded-full">Listo</span>
                  </div>
                  
                  <div className="flex items-start gap-2 text-slate-600 text-sm mb-2">
                    <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" />
                    <span>{orderView(order).address || order.address || 'Sin dirección registrada'}{orderView(order).city ? ` · ${orderView(order).city}` : ''}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-4">
                    <DollarSign size={16} className="text-slate-400 shrink-0" />
                    <span>{paymentLabel(orderView(order).payment)}{order.is_paid ? ' · ya pagado ✓' : ''} - <strong>${Number(order.total || 0).toLocaleString()}</strong></span>
                  </div>

                  <button 
                    onClick={() => takeOrder(order.id)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-sm transition-all active:scale-[0.98]"
                  >
                    Tomar Pedido
                  </button>
                </div>
              ))
            )
          )}

          {activeTab === 'my_orders' && (
            myOrders.filter(o => o.status === 'shipped').length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Navigation size={48} className="mx-auto mb-4 opacity-20" />
                <p className="font-medium">No tienes entregas en curso.</p>
              </div>
            ) : (
              myOrders.filter(o => o.status === 'shipped').map(order => (
                <div key={order.id} className="bg-blue-50 border border-blue-100 rounded-2xl p-4 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-2 h-full bg-blue-500"></div>
                  <div className="flex justify-between items-start mb-3 pr-4">
                    <div>
                      <span className="text-xs font-bold text-blue-400">En Camino • {orderNumber(order)}</span>
                      <h3 className="font-black text-lg text-slate-900 mt-0.5">{orderView(order).name || 'Cliente'}</h3>
                    </div>
                    <span className="bg-blue-200 text-blue-800 text-[10px] uppercase font-bold px-2 py-1 rounded-full">Activo</span>
                  </div>
                  
                  <div className="flex items-start gap-2 text-slate-700 text-sm mb-2">
                    <MapPin size={16} className="text-blue-500 mt-0.5 shrink-0" />
                    <span className="font-medium">{orderView(order).address || order.address || 'Sin dirección'}{orderView(order).city ? ` · ${orderView(order).city}` : ''}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-slate-700 text-sm mb-4">
                    <DollarSign size={16} className="text-blue-500 shrink-0" />
                    <span>{paymentLabel(orderView(order).payment)}{order.is_paid ? ' · ya pagado ✓' : ''} - <strong>${Number(order.total || 0).toLocaleString()}</strong></span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => openWaze(`${orderView(order).address || order.address || ''} ${orderView(order).city}`.trim())}
                      className="bg-white border border-slate-200 text-slate-700 font-bold py-2.5 rounded-xl shadow-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <Navigation size={16} /> Navegar
                    </button>
                    <button 
                      onClick={() => setClosing({ order, kind: 'deliver' })}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 rounded-xl shadow-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={16} /> Entregado
                    </button>
                  </div>
                  <button
                    onClick={() => setClosing({ order, kind: 'fail' })}
                    className="mt-2 w-full bg-white border border-rose-200 text-rose-600 font-bold py-2.5 rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} /> No se pudo entregar
                  </button>
                </div>
              ))
            )
          )}
        </div>

      {closing && (() => {
        const view = orderView(closing.order);
        const total = Number(closing.order.total || 0);
        const money = `$${total.toLocaleString()}`;
        const owesCash = !closing.order.is_paid && !isDigitalPayment(view.payment);
        const reasonOk = failReason && (failReason !== 'otro' || failNote.trim());
        const reasons = [
          { key: 'cliente_cancelo', label: 'El cliente canceló', outcome: 'cancelled_by_client' },
          { key: 'no_contesto', label: 'No contestó / no estaba', outcome: 'cancelled' },
          { key: 'direccion', label: 'No encontré la dirección', outcome: 'cancelled' },
          { key: 'otro', label: 'Otro motivo', outcome: 'cancelled' }
        ];
        const chosen = reasons.find((r) => r.key === failReason);
        return (
          <div className="fixed inset-0 z-30 flex items-end sm:items-center justify-center bg-slate-900/50 p-3" role="dialog" aria-modal="true" aria-label={closing.kind === 'deliver' ? 'Entregar pedido' : 'No se pudo entregar'}>
            <div className="bg-white w-full max-w-md rounded-3xl p-5 shadow-xl space-y-3">
              <p className="text-xs font-bold text-slate-400">{orderNumber(closing.order)} · {view.name || 'Cliente'}</p>
              {closing.kind === 'deliver' ? (
                <>
                  <h2 className="text-lg font-black text-slate-900">{owesCash ? '¿Recibiste el pago?' : 'Confirmar entrega'}</h2>
                  <p className="text-sm text-slate-600">{paymentLabel(view.payment)}{closing.order.is_paid ? ' · ya pagado ✓' : ''} - <strong>{money}</strong></p>
                  {owesCash ? (
                    <div className="space-y-2">
                      <button disabled={closingBusy} onClick={() => closeOrder('delivered', { paid: true })} className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl">Sí, cobré {money}</button>
                      <button disabled={closingBusy} onClick={() => closeOrder('delivered', { paid: false })} className="w-full bg-amber-100 hover:bg-amber-200 disabled:opacity-60 text-amber-800 font-bold py-3 rounded-xl">Entregué, pero no cobré</button>
                    </div>
                  ) : (
                    <>
                      {!closing.order.is_paid && <p className="text-xs text-slate-500">El pago por transferencia lo confirma el negocio al revisar el comprobante.</p>}
                      <button disabled={closingBusy} onClick={() => closeOrder('delivered', { paid: false })} className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl">Confirmar entrega</button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <h2 className="text-lg font-black text-slate-900">¿Qué pasó con el pedido?</h2>
                  <div className="space-y-1.5">
                    {reasons.map((r) => (
                      <label key={r.key} className={`flex items-center gap-2 border rounded-xl px-3 py-2 text-sm cursor-pointer ${failReason === r.key ? 'border-rose-400 bg-rose-50 text-rose-800 font-bold' : 'border-slate-200 text-slate-700'}`}>
                        <input type="radio" name="fail-reason" checked={failReason === r.key} onChange={() => setFailReason(r.key)} />
                        {r.label}
                      </label>
                    ))}
                  </div>
                  {failReason === 'otro' && (
                    <input type="text" maxLength={120} placeholder="Cuéntanos qué pasó" aria-label="Otro motivo" value={failNote} onChange={(e) => setFailNote(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
                  )}
                  <button
                    disabled={closingBusy || !reasonOk}
                    onClick={() => closeOrder(chosen.outcome, { note: failReason === 'otro' ? `Otro: ${failNote.trim()}` : chosen.label })}
                    className="w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-bold py-3 rounded-xl"
                  >
                    Reportar
                  </button>
                </>
              )}
              <button disabled={closingBusy} onClick={() => { setClosing(null); setFailReason(''); setFailNote(''); }} className="w-full text-slate-500 font-bold py-2">Volver</button>
            </div>
          </div>
        );
      })()}
      </main>
    </div>
  );
}
